/**
 * Tirar hacia abajo para recargar, sobre un contenedor que no es el documento.
 *
 * El gesto nativo de iOS y Android sólo existe cuando quien scrollea es la
 * PÁGINA. Aquí scrollea el marco —para que la barra superior se quede fija— y
 * eso deja el gesto fuera de alcance: no hay propiedad que lo devuelva, porque
 * el navegador nunca llega a verlo.
 *
 * Así que se implementa. Lo justo para que se comporte como el de verdad:
 * arranca sólo estando arriba del todo, ofrece resistencia según se estira, y
 * al soltar decide por la distancia recorrida.
 */

/** Cuánto hay que tirar para que cuente. Menos, y se dispara sin querer. */
const THRESHOLD = 72

/** Tope del estirado: pasado esto la goma deja de dar de sí. */
const MAX_PULL = 120

/**
 * La mitad del recorrido del dedo.
 *
 * Sin resistencia el indicador va pegado al dedo y el gesto se siente ligero —
 * se dispara sin querer al scrollear rápido hacia arriba. Con ella hay que
 * tirar de verdad, que es lo que lo convierte en una decisión.
 */
const RESISTANCE = 0.5

export type PullToRefreshOptions = {
  /** Qué hacer al confirmar. Si devuelve promesa, se espera antes de soltar. */
  onRefresh: () => void | Promise<void>
  /** Se llama con la distancia actual (0 = suelto) para pintar el indicador. */
  onPull?: (distance: number) => void
}

export function attachPullToRefresh(node: HTMLElement, options: PullToRefreshOptions): () => void {
  let startY = 0
  let pulling = false
  let distance = 0
  let busy = false

  const report = (value: number) => {
    distance = value
    options.onPull?.(value)
  }

  const onTouchStart = (event: TouchEvent) => {
    // Sólo desde arriba del todo, y sólo con un dedo: con dos es un pellizco
    // para hacer zoom, no un tirón.
    if (busy || node.scrollTop > 0 || event.touches.length !== 1) return
    startY = event.touches[0]?.clientY ?? 0
    pulling = true
  }

  const onTouchMove = (event: TouchEvent) => {
    if (!pulling || busy) return
    const y = event.touches[0]?.clientY ?? 0
    const raw = y - startY

    // Hacia arriba, o si el contenido ya se movió, esto no es un tirón: se
    // devuelve el gesto al desplazamiento normal.
    if (raw <= 0 || node.scrollTop > 0) {
      if (distance !== 0) report(0)
      pulling = false
      return
    }

    // `preventDefault` evita que iOS haga su rebote elástico por debajo. Exige
    // que el oyente no sea pasivo, y por eso se registra con `passive: false`.
    event.preventDefault()
    report(Math.min(raw * RESISTANCE, MAX_PULL))
  }

  const onTouchEnd = () => {
    if (!pulling || busy) return
    pulling = false
    if (distance < THRESHOLD) {
      report(0)
      return
    }
    busy = true
    // Se deja el indicador puesto mientras recarga: soltarlo antes daría la
    // sensación de que el gesto no llegó a contar.
    report(THRESHOLD)
    void Promise.resolve(options.onRefresh()).finally(() => {
      busy = false
      report(0)
    })
  }

  node.addEventListener('touchstart', onTouchStart, { passive: true })
  node.addEventListener('touchmove', onTouchMove, { passive: false })
  node.addEventListener('touchend', onTouchEnd, { passive: true })
  node.addEventListener('touchcancel', onTouchEnd, { passive: true })

  return () => {
    node.removeEventListener('touchstart', onTouchStart)
    node.removeEventListener('touchmove', onTouchMove)
    node.removeEventListener('touchend', onTouchEnd)
    node.removeEventListener('touchcancel', onTouchEnd)
  }
}
